import * as React from 'react';
import { Button } from '../button/button';
import type { ClickableItemProps, ClickableItemListProps } from './clickable-list.types';
import { Body2Typography, SimpleTooltip } from '@components/atoms';
import { cn } from '@utils';

const List = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
	({ className, children, ...props }, ref) => (
		<ul ref={ref} className={cn(className)} data-aid="list" {...props}>
			{children}
		</ul>
	)
);
List.displayName = 'List';

const ClickableItem: React.FC<ClickableItemProps> = ({ row, index }) => {
	const { text, icon, onClick, disabled = false, tooltip } = row;

	const button = (
		<Button
			variant="icon"
			className={cn('h-0 w-full', disabled && 'opacity-50')}
			onClick={onClick}
			disabled={disabled}
			innerContainerClassName="flex items-center justify-between w-full"
		>
			<Body2Typography className="text-primary">{text}</Body2Typography>
			{icon}
		</Button>
	);

	return (
		<li
			aria-labelledby={`${text}-${index}`}
			className="border-b border-borderSeparator pb-2 pt-2 first:pt-0 last:border-b-0 last:pb-0"
		>
			{disabled && tooltip ? (
				<SimpleTooltip content={tooltip} asChild>
					<span className="block w-full cursor-not-allowed!">{button}</span>
				</SimpleTooltip>
			) : (
				button
			)}
		</li>
	);
};
ClickableItem.displayName = 'ClickableItem';

const ClickableList = React.forwardRef<HTMLUListElement, ClickableItemListProps>(({ rows }, ref) => (
	<List ref={ref}>
		{rows.map((row, index) => (
			<ClickableItem key={row.text} row={row} index={index} />
		))}
	</List>
));
ClickableList.displayName = 'ClickableList';

export { ClickableItem, ClickableList };
